import requestService from '@/services/request'
import constants from '@/constants'
import mixpanelService from '@/services/analytics/Mixpanel'
import analyticsService from '@/services/analytics/analytics'
import wagerService from '@/services/wager'
import { createSlice, createSelector } from '@reduxjs/toolkit'
import { transformArrayIntoMap } from '@/util/mapArray'
import { fetchCurrentUser } from '@/store/account'
import { fetchMarketById, updateBetInMarket } from '@/store/markets'
import currencies, { isRealMoney as isCurrencyRealMoney, getMarketType } from '@/util/currencies'
import { toastService } from '@/api/toast'
import { buildErrorToast, resolveErrorMessage } from '@/util/errorMessages'
import { i18n } from '@/locale/i18n'

/**
 * The wager returned by `POST /wagers/` carries the charged value as
 * `total_amount`/`amount`, while list endpoints expose it as
 * `purchases_amount`/`active_purchases_amount` — the fields every position UI reads.
 * Fill the missing one so a freshly created wager renders the same as a fetched one.
 */
const withPurchasesAmount = wager => {
  if (!wager || wager.purchases_amount != null || wager.active_purchases_amount != null) return wager
  const purchasesAmount = wager.total_amount ?? wager.amount
  if (purchasesAmount == null) return wager
  return { ...wager, purchases_amount: purchasesAmount }
}

const initialState = {
  pool: { data: {}, status: {}, error: {} },
  // Separate pools for active and past bets to avoid conflicts
  activeBetsPool: { data: {}, status: {}, error: {} },
  pastBetsPool: { data: {}, status: {}, error: {} },
  summary: {
    data: {},
    status: constants.IDLE,
    error: null,
  },
  activeBets: {
    [constants.REAL_MONEY]: { data: [], status: constants.IDLE, error: null },
    [constants.PLAY_MONEY]: { data: [], status: constants.IDLE, error: null },
  },
  pastBets: {
    [constants.REAL_MONEY]: { data: [], status: constants.IDLE, error: null },
    [constants.PLAY_MONEY]: { data: [], status: constants.IDLE, error: null },
  },
  history: {
    data: {},
    status: {},
    error: {},
    pagination: {},
  },
  transactions: {
    data: {},
    status: {},
    error: {},
    pagination: {},
  },
  userBets: {
    [constants.REAL_MONEY]: { data: {}, status: {}, error: {}, pagination: {} },
    [constants.PLAY_MONEY]: { data: {}, status: {}, error: {}, pagination: {} },
  },
  profileBets: {
    data: {},
    status: constants.IDLE,
    pagination: {},
    profile_id: null,
    error: null,
  },
  lastBet: { data: {}, status: constants.IDLE, error: null },
  lastOrder: { data: {}, status: constants.IDLE, error: null },
  wagerSelected: null,
  recentBets: {
    data: [],
    status: constants.IDLE,
    error: null,
  },
  userOrders: {
    data: [],
    status: constants.IDLE,
    error: null,
    pagination: {},
  },
  portfolioOrders: {
    data: [],
    status: constants.IDLE,
    error: null,
    pagination: {},
  },
  cancelledOrders: {},
  cancelOrder: {
    status: constants.IDLE,
    error: null,
  },
  // Ids of wagers with a sale request in flight, keyed by id.
  sellingBets: {},
}

const emptyArray = []
const emptyObj = {}

// Centralized Error Handling Utilities
/**
 * Extract error information from API error response
 * @param {Object} error - The error object from API
 * @returns {Object|null} - Error info with code and detail, or null if not found
 */
export const getErrorInfo = error => {
  // Try different error formats
  if (error?.error?.errors?.[0]) {
    return {
      code: error.error.errors[0].code,
      detail: error.error.errors[0].detail,
      attr: error.error.errors[0].attr,
    }
  }
  if (error?.errors?.[0]) {
    return {
      code: error.errors[0].code,
      detail: error.errors[0].detail,
      attr: error.errors[0].attr,
    }
  }
  return null
}

/**
 * Error handling configuration for different error types
 */
export const ERROR_HANDLERS = {
  OrderBookConflictingOrders: {
    shouldShowToast: false, // Handled by components with conflict UI
    shouldRethrow: true,
  },
  OrderBookInvalidAmount: {
    shouldShowToast: true,
    toastTitleKey: 'api_errors.titles.place_bet',
    toastMessageKey: 'api_errors.codes.OrderBookInvalidAmount',
    shouldRethrow: true,
  },
  not_authenticated: {
    shouldShowToast: false, // Handled by components with navigation
    shouldRethrow: true,
  },
}

/**
 * Handle order-related errors with centralized logic
 * @param {Object} error - The error object from API
 * @param {Object} options - Configuration options
 * @param {Function} options.onOrderConflict - Callback for order conflict errors
 * @param {Function} options.onInvalidAmount - Callback for invalid amount errors
 * @param {Function} options.onAuthenticationError - Callback for auth errors
 * @param {Function} options.onGenericError - Callback for generic errors
 * @param {boolean} options.showToast - Whether to show toast notifications (default: true)
 * @returns {Object} - Error handling result with handled flag and error info
 */
export const handleOrderError = (error, options = {}) => {
  const {
    onOrderConflict,
    onInvalidAmount,
    onAuthenticationError,
    onGenericError,
    showToast = true,
    defaultTitle = i18n('api_errors.titles.place_bet'),
    defaultMessage = i18n('api_errors.generic.body'),
  } = options

  const errorInfo = getErrorInfo(error)
  if (!errorInfo) {
    // Unknown error format
    if (showToast && onGenericError) {
      onGenericError({ code: 'unknown_error', detail: error?.message || defaultMessage })
    } else if (showToast) {
      toastService('fail', {
        title: defaultTitle,
        body: resolveErrorMessage(error, 'api_errors.generic.body') || defaultMessage,
      })
    }
    return { handled: false, errorInfo: null }
  }

  const handler = ERROR_HANDLERS[errorInfo.code]

  // Handle specific error types
  switch (errorInfo.code) {
    case 'OrderBookConflictingOrders':
      if (onOrderConflict) {
        onOrderConflict(error)
        // Caller should handle the async result if needed
      }
      return { handled: true, errorInfo, shouldRethrow: handler?.shouldRethrow ?? true }

    case 'OrderBookInvalidAmount':
      if (onInvalidAmount) {
        onInvalidAmount(error)
      }
      if (showToast && handler?.shouldShowToast) {
        toastService('fail', {
          title: i18n(handler.toastTitleKey),
          body: i18n(handler.toastMessageKey),
        })
      }
      return { handled: true, errorInfo, shouldRethrow: handler?.shouldRethrow ?? true }

    case 'not_authenticated':
      if (onAuthenticationError) {
        onAuthenticationError(error)
      }
      return { handled: true, errorInfo, shouldRethrow: handler?.shouldRethrow ?? true }

    default:
      // Generic error handling
      if (showToast && onGenericError) {
        onGenericError(errorInfo)
      } else if (showToast) {
        toastService('fail', {
          title: defaultTitle,
          body: resolveErrorMessage(error, 'api_errors.generic.body') || defaultMessage,
        })
      }
      return { handled: false, errorInfo, shouldRethrow: true }
  }
}

// Reducers / Slices

const wagers = createSlice({
  name: 'wagers',
  initialState,
  reducers: {
    getCurrentUserSummaryStart: state => {
      state.summary.status = constants.LOADING
    },
    getCurrentUserSummarySuccess: (state, action) => {
      const { data } = action.payload
      state.summary.status = constants.COMPLETE
      state.summary.data = transformArrayIntoMap(data || [], 'currency')
    },

    getCurrentUserSummaryError: (state, action) => {
      const { error } = action.payload
      state.summary.status = constants.ERROR
      state.summary.error = error
    },

    getCurrentUserBetsStart: (state, action) => {
      const { isRealMoney, isActiveBets } = action.payload
      const marketType = getMarketType(isRealMoney)
      const bets = isActiveBets ? state.activeBets[marketType] : state.pastBets[marketType]
      bets.status = constants.LOADING
    },

    getCurrentUserBetsSuccess: (state, action) => {
      const { isRealMoney, isActiveBets, data, withRefresh } = action.payload
      const marketType = getMarketType(isRealMoney)
      const bets = isActiveBets ? state.activeBets[marketType] : state.pastBets[marketType]

      // Use separate pools for active and past bets to avoid conflicts
      const targetPool = isActiveBets ? state.activeBetsPool : state.pastBetsPool

      if (withRefresh) {
        // Update the appropriate pool only - no interference between active/past
        targetPool.data = transformArrayIntoMap(data.results)
        if (isActiveBets) state.activeBets[marketType].data = [...data.results.map(el => el.id)]
        else state.pastBets[marketType].data = [...data.results.map(el => el.id)]
      } else {
        // Merge with existing data in the appropriate pool
        targetPool.data = { ...targetPool.data, ...transformArrayIntoMap(data.results) }
        if (isActiveBets)
          state.activeBets[marketType].data = state.activeBets[marketType].data.concat([
            ...data.results.map(el => el.id),
          ])
        else
          state.pastBets[marketType].data = state.pastBets[marketType].data.concat([...data.results.map(el => el.id)])
      }
      bets.status = constants.COMPLETE
      bets.pagination = data.pagination
    },

    getCurrentUserBetsError: (state, action) => {
      const { isRealMoney, isActiveBets, error } = action.payload
      const marketType = getMarketType(isRealMoney)
      const bets = isActiveBets ? state.activeBets[marketType] : state.pastBets[marketType]

      bets.error = error
      bets.status = constants.ERROR
    },

    getUserBetStart: (state, action) => {
      const { betId } = action.payload
      state.pool.status[betId] = constants.LOADING
    },

    getUserBetSuccess: (state, action) => {
      const { bet } = action.payload
      state.pool.status[bet.id] = constants.COMPLETE
      state.pool.data[bet.id] = bet
    },

    getUserBetError: (state, action) => {
      const { betId, error } = action.payload
      state.pool.error[betId] = error
      state.pool.status[betId] = constants.ERROR
    },

    getUserWagersStart: (state, action) => {
      const { userId } = action.payload
      state.profileBets.profile_id = userId
      state.profileBets.status = constants.LOADING
    },

    getUserWagersSuccess: (state, action) => {
      const { results, pagination } = action.payload
      state.profileBets.data = results
      state.profileBets.pagination = pagination
      state.profileBets.status = constants.COMPLETE
    },
    resetUserWagersSuccess: state => {
      state.profileBets.data = {}
      state.profileBets.pagination = {}
      state.profileBets.status = constants.IDLE
    },

    getUserWagersError: (state, action) => {
      const { error } = action.payload
      state.profileBets.error = error
      state.profileBets.status = constants.ERROR
    },

    sellBetStart: (state, action) => {
      const { betId } = action.payload
      state.pool.status[betId] = constants.LOADING
      state.pool.error[betId] = null
      if (!state.sellingBets) state.sellingBets = {}
      state.sellingBets[betId] = true
    },

    sellBetSuccess: (state, action) => {
      const { bet } = action.payload
      state.pool.status[bet.id] = constants.COMPLETE
      state.pool.data[bet.id] = bet
      if (state.sellingBets) delete state.sellingBets[bet.id]

      const marketType = getMarketType(bet.currency !== 'OOM')
      if (state.activeBets[marketType].data.length) {
        state.activeBets[marketType].data = state.activeBets[marketType].data.filter(id => id !== bet.id)
      }

      if (state.pastBets[marketType].data.length) {
        state.pastBets[marketType].data = [bet.id, ...state.pastBets[marketType].data]
      }
    },

    sellBetError: (state, action) => {
      const { betId, error } = action.payload
      state.pool.status[betId] = constants.ERROR
      state.pool.error[betId] = error
      if (state.sellingBets) delete state.sellingBets[betId]
    },

    getBetHistoryStart: (state, action) => {
      const { questionId } = action.payload
      state.history.status[questionId] = constants.LOADING
    },

    getBetHistorySuccess: (state, action) => {
      const { questionId, bets, pagination, clear } = action.payload

      state.history.status[questionId] = constants.COMPLETE
      if (!state.history.data[questionId]) {
        state.history.data[questionId] = []
      }
      if (clear) state.history.data[questionId] = [...bets]
      else state.history.data[questionId].push(...bets)
      state.history.pagination[questionId] = pagination
    },

    getBetHistoryError: (state, action) => {
      const { questionId, error } = action.payload
      state.history.error[questionId] = error
    },

    resetBetHistory: (state, action) => {
      const { questionId } = action.payload
      state.history.error[questionId] = null
      state.history.pagination[questionId] = null
      state.history.data[questionId] = []
      state.history.status[questionId] = constants.IDLE
    },

    getTransactionsStart: (state, action) => {
      const { questionId } = action.payload
      state.transactions.status[questionId] = constants.LOADING
    },

    getTransactionsSuccess: (state, action) => {
      const { questionId, transactions, pagination, clear } = action.payload

      state.transactions.status[questionId] = constants.COMPLETE
      if (!state.transactions.data[questionId]) {
        state.transactions.data[questionId] = []
      }
      if (clear) state.transactions.data[questionId] = [...transactions]
      else state.transactions.data[questionId].push(...transactions)
      state.transactions.pagination[questionId] = pagination
    },

    getTransactionsError: (state, action) => {
      const { questionId, error } = action.payload
      state.transactions.error[questionId] = error
    },

    resetTransactions: (state, action) => {
      const { questionId } = action.payload
      state.transactions.error[questionId] = null
      state.transactions.pagination[questionId] = null
      state.transactions.data[questionId] = []
      state.transactions.status[questionId] = constants.IDLE
    },

    getWagerSelected: (state, action) => {
      state.wagerSelected = action.payload
    },
    clearLastBet: state => {
      state.lastBet = { data: {}, status: constants.IDLE, error: null }
    },

    createBetStart: state => {
      state.lastBet.status = constants.LOADING
    },

    createBetSuccess: (state, action) => {
      const { bet } = action.payload
      state.lastBet.data = bet
      state.lastBet.status = constants.COMPLETE
      state.pool.data[bet.id] = bet
    },

    createBetError: (state, action) => {
      const { error } = action.payload
      state.lastBet.error = error
      state.lastBet.status = constants.ERROR
    },

    createLimitOrderStart: state => {
      state.lastOrder.status = constants.LOADING
      state.lastOrder.error = null
    },

    createLimitOrderSuccess: (state, action) => {
      const { order } = action.payload
      state.lastOrder.data = order
      state.lastOrder.status = constants.COMPLETE
    },

    createLimitOrderError: (state, action) => {
      const { error } = action.payload
      state.lastOrder.error = error
      state.lastOrder.status = constants.ERROR
    },

    addActiveBetId: (state, action) => {
      const { isRealMoney, bet } = action.payload
      const marketType = getMarketType(isRealMoney)

      if (state.activeBets[marketType].status === constants.COMPLETE) {
        state.activeBets[marketType].data.unshift(bet.id)
      }
    },
    refreshBet: (state, action) => {
      const { bet } = action.payload
      state.activeBets[constants.REAL_MONEY].data = {
        ...state.activeBets[constants.REAL_MONEY].data.find(b => b.id === bet.id),
        ...bet,
      }
      state.pastBets[constants.REAL_MONEY].data = {
        ...state.pastBets[constants.REAL_MONEY].data.find(b => b.id === bet.id),
        ...bet,
      }
      state.activeBets[constants.PLAY_MONEY].data = {
        ...state.activeBets[constants.PLAY_MONEY].data.find(b => b.id === bet.id),
        ...bet,
      }
      state.pastBets[constants.PLAY_MONEY].data = {
        ...state.pastBets[constants.PLAY_MONEY].data.find(b => b.id === bet.id),
        ...bet,
      }
    },

    getUserBetsStart: (state, action) => {
      const { isRealMoney, user } = action.payload
      if (!user) {
        return
      }

      const marketType = getMarketType(isRealMoney)
      state.userBets[marketType].status[user.id] = constants.LOADING
    },

    getUserBetsSuccess: (state, action) => {
      const { isRealMoney, user, results, pagination, replace } = action.payload
      if (!user) {
        return
      }

      const marketType = getMarketType(isRealMoney)
      if (!state.userBets[marketType].data[user.id]) {
        state.userBets[marketType].data[user.id] = []
      }

      // replace: fresh search/reset shows exactly the API response; append: load-more pagination
      state.userBets[marketType].data[user.id] = replace
        ? results
        : [...state.userBets[marketType].data[user.id], ...results]
      state.userBets[marketType].pagination[user.id] = pagination
      state.userBets[marketType].status[user.id] = constants.COMPLETE
    },

    getUserBetsError: (state, action) => {
      const { isRealMoney, error, user } = action.payload

      const marketType = getMarketType(isRealMoney)
      state.userBets[marketType].data[user.id] = error
      state.userBets[marketType].status[user.id] = constants.ERROR
    },

    resetWagers: state => {
      for (const i in initialState) {
        state[i] = { ...initialState[i] }
      }
    },

    fetchRecentBetsStart: state => {
      state.recentBets.status = constants.LOADING
    },

    fetchRecentBetsSuccess: (state, action) => {
      const { results } = action.payload

      if (!results || !Array.isArray(results)) {
        state.recentBets.status = constants.COMPLETE
        return
      }

      // Filter out any bets without valid IDs
      const validResults = results.filter(bet => bet && bet.id)

      if (validResults.length === 0) {
        state.recentBets.status = constants.COMPLETE
        return
      }

      // Create a set of existing bet IDs for duplicate prevention
      const existingIds = new Set(state.recentBets.data.filter(bet => bet && bet.id).map(bet => bet.id))

      // Filter out duplicates from new results
      const newResults = validResults.filter(bet => !existingIds.has(bet.id))

      // Combine new results with existing data (new ones first)
      const combinedResults = [...newResults, ...state.recentBets.data]

      // Limit to maximum 6 items by taking the most recent ones
      state.recentBets.data = combinedResults.slice(0, 6)
      state.recentBets.status = constants.COMPLETE
    },

    fetchRecentBetsError: (state, action) => {
      state.recentBets.error = action.payload.error
      state.recentBets.status = constants.ERROR
    },

    fetchUserOrdersStart: state => {
      state.userOrders.status = constants.LOADING
    },

    fetchUserOrdersSuccess: (state, action) => {
      const { results, pagination, clear } = action.payload

      if (clear) {
        state.userOrders.data = results
      } else {
        // Filter out duplicates by checking if ID already exists
        const existingIds = new Set(state.userOrders.data.map(order => order.id))
        const newOrders = results.filter(order => !existingIds.has(order.id))
        state.userOrders.data = [...state.userOrders.data, ...newOrders]
      }

      // Update pagination state
      if (pagination) {
        state.userOrders.pagination = pagination
      }

      state.userOrders.status = constants.COMPLETE
    },

    fetchUserOrdersError: (state, action) => {
      state.userOrders.error = action.payload.error
      state.userOrders.status = constants.ERROR
    },

    fetchPortfolioOrdersStart: state => {
      state.portfolioOrders.status = constants.LOADING
    },

    fetchPortfolioOrdersSuccess: (state, action) => {
      const { results, pagination, clear } = action.payload

      if (clear) {
        state.portfolioOrders.data = results
      } else {
        // Filter out duplicates by checking if ID already exists
        const existingIds = new Set(state.portfolioOrders.data.map(order => order.id))
        const newOrders = results.filter(order => !existingIds.has(order.id))
        state.portfolioOrders.data = [...state.portfolioOrders.data, ...newOrders]
      }

      // Update pagination state
      if (pagination) {
        state.portfolioOrders.pagination = pagination
      }

      state.portfolioOrders.status = constants.COMPLETE
    },

    fetchPortfolioOrdersError: (state, action) => {
      state.portfolioOrders.error = action.payload.error
      state.portfolioOrders.status = constants.ERROR
    },

    resetUserOrders: state => {
      state.userOrders = {
        data: [],
        status: constants.IDLE,
        error: null,
        pagination: {},
      }
    },

    markOrderAsCancelled: (state, action) => {
      const { orderId } = action.payload
      // Add timestamp to track when order was cancelled
      if (!state.cancelledOrders) {
        state.cancelledOrders = {}
      }
      state.cancelledOrders[orderId] = Date.now()
    },

    clearCancelledOrder: (state, action) => {
      const { orderId } = action.payload
      if (state.cancelledOrders && state.cancelledOrders[orderId]) {
        delete state.cancelledOrders[orderId]
      }
    },

    removePortfolioOrder: (state, action) => {
      const { orderId } = action.payload
      state.portfolioOrders.data = state.portfolioOrders.data.filter(order => order.id !== orderId)

      // Update pagination total if available
      if (state.portfolioOrders.pagination && state.portfolioOrders.pagination.total > 0) {
        state.portfolioOrders.pagination.total -= 1
      }
    },

    // Cancel Order State Management
    cancelOrderStart: (state, action) => {
      state.cancelOrder.status = constants.LOADING
      state.cancelOrder.error = null
    },

    cancelOrderSuccess: (state, action) => {
      state.cancelOrder.status = constants.COMPLETE
      state.cancelOrder.error = null
    },

    cancelOrderError: (state, action) => {
      const { error } = action.payload
      state.cancelOrder.status = constants.ERROR
      state.cancelOrder.error = error
    },

    /* A market sale is placed as an ask order, so nothing in the response carries the
       wager it came out of and the market's wagers only refresh a round trip later.
       These two mark that gap, so the position can be left out of the surfaces that
       offer it for sale while its own sale is still going through. */
    markWagerSelling: (state, action) => {
      const { betId } = action.payload
      if (!state.sellingBets) state.sellingBets = {}
      state.sellingBets[betId] = true
    },

    clearWagerSelling: (state, action) => {
      const { betId } = action.payload
      if (state.sellingBets) delete state.sellingBets[betId]
    },
  },
})

export const { getCurrentUserSummaryStart, getCurrentUserSummarySuccess, getCurrentUserSummaryError } = wagers.actions
export const { getCurrentUserBetsStart, getCurrentUserBetsSuccess, getCurrentUserBetsError } = wagers.actions
export const { getUserBetStart, getUserBetSuccess, getUserBetError } = wagers.actions
export const { getUserWagersStart, getUserWagersSuccess, getUserWagersError, resetUserWagersSuccess } = wagers.actions
export const { sellBetStart, sellBetSuccess, sellBetError } = wagers.actions
export const { markWagerSelling, clearWagerSelling } = wagers.actions
export const { resetBetHistory, getBetHistoryStart, getBetHistorySuccess, getBetHistoryError, getWagerSelected } =
  wagers.actions
export const { resetTransactions, getTransactionsStart, getTransactionsSuccess, getTransactionsError } = wagers.actions
export const { clearLastBet, createBetStart, createBetError, createBetSuccess, addActiveBetId, refreshBet } =
  wagers.actions
export const { getUserBetsStart, getUserBetsSuccess, getUserBetsError, resetWagers } = wagers.actions
export const { fetchRecentBetsStart, fetchRecentBetsSuccess, fetchRecentBetsError } = wagers.actions
export const {
  fetchUserOrdersStart,
  fetchUserOrdersSuccess,
  fetchUserOrdersError,
  fetchPortfolioOrdersStart,
  fetchPortfolioOrdersSuccess,
  fetchPortfolioOrdersError,
  resetUserOrders,
  markOrderAsCancelled,
  clearCancelledOrder,
  removePortfolioOrder,
  cancelOrderStart,
  cancelOrderSuccess,
  cancelOrderError,
} = wagers.actions
export const { createLimitOrderStart, createLimitOrderSuccess, createLimitOrderError } = wagers.actions
export default wagers.reducer

// Selectors:
export const selectPortfolioSummary = () => state => {
  return state.wagers.summary.data
}

export const selectPortfolioSummaryStatus = () => state => {
  return state.wagers.summary.status
}

// Memoized so useSelector gets stable reference (fixes SSR "selector returned different result")
export const selectUserBets = (marketType, isActiveBets) =>
  createSelector(
    [
      state => getBetsReference(state, marketType, isActiveBets).data,
      state => (isActiveBets ? state.wagers.activeBetsPool.data : state.wagers.pastBetsPool.data),
    ],
    (order, pool) => (order.length === 0 ? emptyArray : order.map(id => pool[id]))
  )
export const selectUserActiveBetsByQuestion = () => state => {
  const order = [
    ...state.wagers.activeBets[constants.REAL_MONEY].data,
    ...state.wagers.activeBets[constants.PLAY_MONEY].data,
  ]
  // Use active bets pool for active bets only
  return [...order.map(id => state.wagers.activeBetsPool.data[id])]
}

export const selectUserBetsStatus = (marketType, isActiveBets) => state => {
  return getBetsReference(state, marketType, isActiveBets).status
}

export const selectUserBetsPagination = (marketType, isActiveBets) => state => {
  return getBetsReference(state, marketType, isActiveBets).pagination || {}
}

export const selectUserBetsError = (marketType, isActiveBets) => state => {
  return getBetsReference(state, marketType, isActiveBets).error
}

export const selectBetById = betId => state => {
  // Check both pools since we don't know if it's active or past bet
  return (
    state.wagers.activeBetsPool.data[betId] || state.wagers.pastBetsPool.data[betId] || state.wagers.pool.data[betId]
  )
}

/* Map of wager id -> true while its sale request is in flight. A wager in here is
   already spoken for, so surfaces that offer a position for sale leave it out until
   the refreshed market says what is left of it. */
export const selectSellingBets = () => state => state.wagers.sellingBets || emptyObj

export const selectBetStatusById = betId => state => {
  return state.wagers.pool.status[betId] || constants.IDLE
}

export const selectBetErrorById = betId => state => {
  return state.wagers.pool.error[betId]
}

export const selectBetHistory = questionId => state => {
  return state.wagers.history.data[questionId] || emptyArray
}

export const selectBetHistoryPagination = questionId => state => {
  return state.wagers.history.pagination[questionId]
}

export const selectBetHistoryStatus = questionId => state => {
  return state.wagers.history.status[questionId]
}

export const selectBetHistoryByUser = (marketType, user) => state => {
  if (!user) {
    return emptyArray
  }

  return state.wagers.userBets[marketType].data[user.id] || emptyArray
}

export const selectBetHistoryStatusByUser = (marketType, user) => state => {
  if (!user) {
    return constants.IDLE
  }

  return state.wagers.userBets[marketType].status[user.id]
}

export const selectBetHistoryPaginationByUser = (marketType, user) => state => {
  if (!user) {
    return emptyObj
  }

  return state.wagers.userBets[marketType].pagination[user.id] || emptyObj
}

export const selectBetHistoryErrorByUser = (marketType, user) => state => {
  if (!user) {
    return null
  }

  return state.wagers.userBets[marketType].error[user.id]
}

export const selectTransactions = questionId => state => {
  return state.wagers.transactions.data[questionId] || emptyArray
}

export const selectTransactionsPagination = questionId => state => {
  return state.wagers.transactions.pagination[questionId]
}

export const selectTransactionsStatus = questionId => state => {
  return state.wagers.transactions.status[questionId]
}

export const selectWagerSelected = () => state => state.wagers.wagerSelected

export const selectLastBet = () => state => state.wagers.lastBet.data

export const selectLastBetStatus = () => state => state.wagers.lastBet.status

export const selectLastBetError = () => state => state.wagers.lastBet.error

export const selectUserWagers = () => state => state.wagers.profileBets.data

export const selectUserWagersStatus = () => state => state.wagers.profileBets.status

export const selectUserWagersError = () => state => state.wagers.profileBets.error

export const selectUserWagersPagination = () => state => state.wagers.profileBets.pagination

export const selectRecentBets = () => state => state.wagers.recentBets.data

export const selectRecentBetsStatus = () => state => state.wagers.recentBets.status

export const selectRecentBetsError = () => state => state.wagers.recentBets.error

export const selectUserOrders = () => state => state.wagers.userOrders.data

export const selectUserOrdersStatus = () => state => state.wagers.userOrders.status

export const selectUserOrdersError = () => state => state.wagers.userOrders.error

export const selectUserOrdersPagination = () => state => state.wagers.userOrders.pagination

export const selectPortfolioOrders = () => state => state.wagers.portfolioOrders.data

export const selectPortfolioOrdersStatus = () => state => state.wagers.portfolioOrders.status

export const selectPortfolioOrdersError = () => state => state.wagers.portfolioOrders.error

export const selectPortfolioOrdersPagination = () => state => state.wagers.portfolioOrders.pagination

export const selectLastOrder = () => state => state.wagers.lastOrder.data

export const selectLastOrderStatus = () => state => state.wagers.lastOrder.status

export const selectLastOrderError = () => state => state.wagers.lastOrder.error

export const selectCancelledOrders = () => state => state.wagers.cancelledOrders || {}

// Cancel Order Selectors
export const selectCancelOrderStatus = () => state => state.wagers.cancelOrder.status
export const selectCancelOrderError = () => state => state.wagers.cancelOrder.error

// Utils:
const getBetsReference = (state, marketType, isActiveBets) =>
  isActiveBets ? state.wagers.activeBets[marketType] : state.wagers.pastBets[marketType]

export const getAllCampains = () => JSON.parse(localStorage.getItem('campains') || '[]')

// Thunks:
/**
 * Fetch user portfolio summary. Contains information like profit, return and value invested.
 */
export const fetchPortfolioSummary = () => async dispatch => {
  dispatch(getCurrentUserSummaryStart())
  try {
    var response = await requestService.get(`/portfolio/summary/`)
  } catch (error) {
    dispatch(getCurrentUserSummaryError({ error }))
    return
  }
  const data = await response.json()
  dispatch(getCurrentUserSummarySuccess({ data }))
}

/**
 * Retrieve active user bets. `options` can contain an entry named `past_bets` instead, in order to
 * retrieve resolved and sold bets.
 */
export const fetchCurrentUserBets = options => async dispatch => {
  const isRealMoney = options.currency_mode === constants.REAL_MONEY
  const isActiveBets = options.status_group === 'active'
  delete options.currency_mode
  const params = requestService.parseFilters(options)

  dispatch(getCurrentUserBetsStart({ isRealMoney, isActiveBets }))
  try {
    var response = await requestService.get(`/portfolio/?${params.toString()}`)
  } catch (error) {
    dispatch(getCurrentUserBetsError({ error, isRealMoney, isActiveBets }))
    return
  }
  const data = await response.json()
  dispatch(getCurrentUserBetsSuccess({ data, isRealMoney, isActiveBets, withRefresh: options.withRefresh }))
  return data
}

/**
 * Get a single bet information using its id.
 */
export const fetchUserBet = betId => async dispatch => {
  dispatch(getUserBetStart({ betId }))
  try {
    var response = await requestService.get(`/wagers/${betId}/`)
  } catch (error) {
    dispatch(getUserBetError({ betId, error }))
    return
  }
  const bet = await response.json()
  dispatch(getUserBetSuccess({ bet }))
}

/**
 * Get a single bet information using its id.
 */
export const fetchPositionsByUser = (userId, options) => async dispatch => {
  const params = requestService.parseFilters(options)
  dispatch(getUserWagersStart({ userId }))
  try {
    // !TODO : add the use the option object
    var response = await requestService.get(`/wagers/?user=${userId}&${params.toString()}`)
  } catch (error) {
    dispatch(getUserWagersError({ error }))
    return
  }
  const { results, pagination } = await response.json()
  dispatch(getUserWagersSuccess({ results, pagination }))
}
/**
 * Get Recent Bets
 */
export const fetchRecentBets = () => async dispatch => {
  dispatch(fetchRecentBetsStart())
  try {
    var response = await requestService.get(`/wagers/latest_purchase_actions/`)
  } catch (error) {
    dispatch(fetchRecentBetsError({ error }))
    return
  }
  const results = await response.json()

  dispatch(fetchRecentBetsSuccess({ results }))
}

/**
 * Get a single bet information using its id.
 */
export const getUpdatesForMarketBet = marketID => async dispatch => {
  try {
    var response = await requestService.get(`/questions/${marketID}/wagers/`)
  } catch (error) {
    return error
  }
  const bets = await response.json()
  bets.results.forEach(bet => dispatch(refreshBet({ bet })))
}

/**
 * Sell a given number of shares for a bet id.
 */
export const sellBet =
  (betId, shares, amount, marketType, bet_location, wholeShare = 0) =>
  async dispatch => {
    dispatch(sellBetStart({ betId }))
    try {
      const body = shares === wholeShare ? {} : { shares, amount, bet_location }
      var request = await requestService.patch(`/wagers/${betId}/`, body)
    } catch (err) {
      dispatch(sellBetError({ err, betId }))
      throw err
    }
    const bet = await request.json()
    await dispatch(fetchMarketById(bet.question.id, marketType, true))
    dispatch(sellBetSuccess({ bet }))
    dispatch(fetchCurrentUser(true))
    toastService('wagerSell', bet)

    mixpanelService.sell(bet)
    return bet
  }

/**
 * Fetch bet history by question
 */
export const fetchQuestionBetHistory =
  (question, options = {}, clear = false) =>
  async dispatch => {
    const isRealMoney = options.currency_mode === constants.REAL_MONEY
    const params = requestService.parseFilters({ ...options })
    const questionId = question.id

    dispatch(getBetHistoryStart({ isRealMoney, questionId }))
    try {
      var request = await requestService.get(`/questions/${question.id}/actions/?${params.toString()}`)
    } catch (error) {
      dispatch(getBetHistoryError({ isRealMoney, error, questionId }))
      return
    }

    const response = await request.json()
    dispatch(
      getBetHistorySuccess({ isRealMoney, questionId, bets: response.results, pagination: response.pagination, clear })
    )
  }

/**
 * Fetch user transactions by question (my_bets=1)
 */
export const fetchQuestionTransactions =
  (question, options = {}, clear = false) =>
  async dispatch => {
    const questionId = question.id

    // Always set my_bets=1 for transactions
    const transactionOptions = {
      ...options,
      my_bets: 1,
      limit: options.limit || 20,
    }

    const params = requestService.parseFilters(transactionOptions)

    dispatch(getTransactionsStart({ questionId }))
    try {
      var request = await requestService.get(`/questions/${question.id}/actions/?${params.toString()}`)
    } catch (error) {
      dispatch(getTransactionsError({ questionId, error }))
      return
    }

    const response = await request.json()
    dispatch(
      getTransactionsSuccess({
        questionId,
        transactions: response.results,
        pagination: response.pagination,
        clear,
      })
    )
  }

/**
 * Fetch bet history by user id and market type.
 * Currently only the current user can see their own bets for real money
 */
export const fetchUserBetHistory =
  (user, options = {}) =>
  async dispatch => {
    // `replace` is store-only (fresh search/reset vs. load-more append) — keep it out of the query string
    const { replace = false, ...apiOptions } = options
    const isRealMoney = apiOptions.currency_mode === constants.REAL_MONEY
    const params = requestService.parseFilters({ ...apiOptions })

    dispatch(getUserBetsStart({ isRealMoney, user }))
    try {
      var request = await requestService.get(`/users/${user.id}/actions/?${params.toString()}`)
    } catch (error) {
      dispatch(getUserBetsError({ isRealMoney, error, user }))
      return
    }

    const { results, pagination } = await request.json()
    dispatch(getUserBetsSuccess({ isRealMoney, user, results, pagination, replace }))
  }

export const placeBet =
  (outcomeId, shares, amount, amountInUsd, currencyId, rate_update_code = '', quickbet, place = '', position = constants.position.LONG) =>
  async dispatch => {
    dispatch(createBetStart())
    try {
      var response = await requestService.post(`/wagers/`, {
        outcome: outcomeId,
        shares: wagerService.roundPrice(shares, 16), // Passing RAW shares to server
        amount: amount,
        currency: currencyId,
        rate_update_code,
        bet_location: place,
        position,
      })
    } catch (err) {
      dispatch(createBetError({ err }))
      throw err.error
    }

    const bet = withPurchasesAmount(await response.json())

    dispatch(createBetSuccess({ bet }))
    toastService('wagerBuy', {
      title: i18n('place_bet.bought'),
      question: bet.question,
    })
    dispatch(fetchCurrentUser(true))
    await dispatch(addActiveBetId({ isRealMoney: isCurrencyRealMoney(currencyId), bet }))
    await dispatch(updateBetInMarket({ bet, marketId: bet.question.id }))

    const all_campains = getAllCampains()
    mixpanelService.bet(bet, amountInUsd)
    analyticsService.trackWagerBuy({
      productType: currencyId === currencies.OOM.id ? 'Play Money' : 'Real Money',
      currency: currencyId,
      amount: bet.total_amount,
      amountInUsd,
      all_campains,
    })

    return bet
  }

/**
 * Place a limit order for a given outcome
 * @param {number|string} outcomeId - The ID of the outcome
 * @param {number} shares - Number of shares (negative for short positions)
 * @param {number} price - The limit price per share
 * @param {string} currencyId - The currency code (e.g., 'BTC', 'USDC', 'OOM')
 * @param {string} marketType - The market type (constants.REAL_MONEY or constants.PLAY_MONEY)
 * @param {string} position - The position type ('long' for long or 'short' for short)
 * @param {string} expiration - The expiration option ('until_cancelled', 'end_of_day', 'custom')
 * @param {Date} [expirationDate] - The custom expiration date (if expiration is 'custom')
 * @param {string} [place] - The source of the bet placement
 * @returns {Promise} - A promise that resolves to the created limit order
 */
export const placeLimitOrder =
  (
    outcomeId,
    shares,
    price,
    currencyId,
    position = constants.position.LONG,
    expiration = 'until_cancelled', 
    expirationDate = null,
    place = '',
    side = 'bid',
    onSuccess = () => {},
    amount = null,
    cancelConflictingOrders = false
  ) =>
  async dispatch => {
    dispatch(createLimitOrderStart())

    try {
      // Call the service method to place the limit order
      const response = await wagerService.placeLimitOrder(
        outcomeId,
        shares,
        price,
        currencyId,
        side,
        position,
        expiration,
        expirationDate,
        place,
        amount,
        cancelConflictingOrders
      )

      const order = await response.json()
      dispatch(createLimitOrderSuccess({ order }))

      // Update user balance
      dispatch(fetchCurrentUser(true))

      // Track analytics
      // const all_campains = getAllCampains()
      // mixpanelService.track('LimitOrderPlaced', {
      //   outcome: outcomeId,
      //   shares: shares,
      //   price: price,
      //   currency: currencyId,
      //   side: side,
      //   position: position,
      // })

      // analyticsService.track('LimitOrderPlaced', {
      //   productType: currencyId === currencies.OOM.id ? 'Play Money' : 'Real Money',
      //   currency: currencyId,
      //   shares: shares,
      //   price: price,
      //   all_campains,
      // })
      onSuccess()

      return order
    } catch (err) {
      dispatch(createLimitOrderError({ error: err }))

      // Use centralized error handling
      const result = handleOrderError(err, {
        showToast: true,
        defaultTitle: i18n('api_errors.titles.place_bet'),
        defaultMessage: i18n('api_errors.generic.body'),
      })

      // Re-throw error so components can handle it (e.g., order conflicts)
      if (result.shouldRethrow !== false) {
        throw err
      }
    }
  }

export const checkMovingForwardSellingShares = async (wagerId, shares) => {
  try {
    var response = await requestService.get(`/wagers/${wagerId}/get_partial_amount_on_sell/?shares=${shares}`)
  } catch (err) {
    // Error occurred
  }

  return response.json()
}

/**
 * Fetch recent bets across all markets
 * @param {Object} options - Filtering options
 * @param {number} options.limit - Number of bets to fetch
 */
export const fetchBetHistory =
  (options = {}) =>
  async dispatch => {
    dispatch(fetchRecentBetsStart())

    try {
      // Use the existing API service for fetching bet history
      const params = requestService.parseFilters({ ...options })
      const request = await requestService.get(`/actions/?${params.toString()}`)
      const response = await request.json()

      dispatch(fetchRecentBetsSuccess({ results: response.results }))
    } catch (error) {
      dispatch(fetchRecentBetsError({ error }))
    }
  }

/**
 * Fetch user orders for market page
 * @param {Object} options - Filtering options
 * @param {boolean} options.clear - Whether to clear existing orders before adding new ones
 * @param {string} options.status - Filter by order status ('open', 'filled', 'cancelled','partially_filled')
 * @param {string} options.currency - Filter by currency
 * @param {number} options.offset - Page number for pagination
 * @param {number} options.limit - Number of items per page
 */
export const fetchUserOrders =
  (options = {}) =>
  async dispatch => {
    dispatch(fetchUserOrdersStart())

    try {
      const clear = options.clear // Extract clear before parseFilters
      const params = requestService.parseFilters(options)
      const response = await requestService.get(`/orders/?${params.toString()}`)
      const data = await response.json()

      dispatch(
        fetchUserOrdersSuccess({
          results: data.results || data,
          pagination: data.pagination,
          clear,
        })
      )

      return data
    } catch (error) {
      console.error('Error fetching user orders:', error)
      dispatch(fetchUserOrdersError({ error }))
      throw error
    }
  }

/**
 * Fetch portfolio orders (all user orders across all markets)
 * @param {Object} options - Filtering options
 * @param {boolean} options.clear - Whether to clear existing orders before adding new ones
 * @param {string} options.status - Filter by order status ('open', 'filled', 'cancelled','partially_filled')
 * @param {string} options.currency - Filter by currency
 * @param {number} options.offset - Page number for pagination
 * @param {number} options.limit - Number of items per page
 *
 */
export const fetchPortfolioOrders =
  (options = {}) =>
  async dispatch => {
    dispatch(fetchPortfolioOrdersStart())

    try {
      const clear = options.clear // Extract clear before parseFilters
      const params = requestService.parseFilters(options)
      // Call /orders/ endpoint for all user orders across markets
      const response = await requestService.get(`/orders/?${params.toString()}`)
      const data = await response.json()

      dispatch(
        fetchPortfolioOrdersSuccess({
          results: data.results || data,
          pagination: data.pagination,
          clear,
        })
      )

      return data
    } catch (error) {
      dispatch(fetchPortfolioOrdersError({ error: error.message || 'Failed to fetch portfolio orders' }))
    }
  }

/**
 * Cancel a user order
 * @param {string|number} orderId - The ID of the order to cancel
 * @param {string|number} questionId - The ID of the question (optional, for refetching specific question orders)
 */
export const cancelUserOrder =
  (orderId, questionId = null) =>
  async dispatch => {
    // Start the cancel process
    dispatch(cancelOrderStart({ orderId }))

    try {
      // Mark order as cancelled to prevent false fill notifications
      dispatch(markOrderAsCancelled({ orderId }))

      await requestService.patch(`/orders/${orderId}/cancel/`)

      // Refresh the orders list after cancellation
      const fetchOptions = { clear: true }
      if (questionId) {
        fetchOptions.question = questionId
        dispatch(fetchUserOrders(fetchOptions))
      } else {
        // If no questionId, refresh portfolio orders
        dispatch(fetchPortfolioOrders(fetchOptions))
      }

      // Clear the cancelled flag after a delay to prevent memory leaks
      setTimeout(() => {
        dispatch(clearCancelledOrder({ orderId }))
      }, 3000)

      // Mark cancel as successful
      dispatch(cancelOrderSuccess({ orderId }))

      // Remove the order from portfolio orders immediately
      dispatch(removePortfolioOrder({ orderId }))

      toastService('success', {
        title: i18n('orders.order_cancelled.title'),
        body: i18n('orders.order_cancelled.body'),
        autoClose: 3000,
      })

      return true
    } catch (error) {
      // Clear the cancelled flag if there was an error
      dispatch(clearCancelledOrder({ orderId }))

      // Mark cancel as failed
      dispatch(cancelOrderError({ orderId, error }))

      toastService('fail', {
        ...buildErrorToast(error, 'api_errors.titles.cancel_order'),
        autoClose: 3000,
      })

      throw error
    }
  }
